import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { ShieldAlert, Loader2, CheckCircle2, XCircle } from "lucide-react";
import "./AdminPartnerRecovery.css";

/**
 * Public "Secure My Organization" landing page.
 *
 * Reached via the security-notification email link:
 *   {base}/partner-recovery/self-lockdown?t=<signed-token>
 *
 * No authentication required. The token is HMAC-signed with a 48h TTL and
 * scopes the action to a single partner organization. On confirm the page
 * calls POST /api/partner-recovery/self-lockdown/{token} which:
 *   • marks the org emergency_disabled
 *   • revokes every API key + OAuth secret
 *   • disables every webhook signing secret
 *   • kills every active portal session
 *   • writes an immutable audit row (public actor)
 */
export default function PublicPartnerLockdown() {
  const [sp] = useSearchParams();
  const token = sp.get("t") || "";
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (!token) {
      setError("Missing lockdown token. Use the link from the security notification email.");
      setLoading(false);
      return;
    }
    api.get(`/partner-recovery/self-lockdown/${encodeURIComponent(token)}/preview`)
      .then((r) => setPreview(r.data))
      .catch((e) => setError(formatApiError(e)))
      .finally(() => setLoading(false));
  }, [token]);

  const confirm = async () => {
    setSubmitting(true);
    try {
      await api.post(`/partner-recovery/self-lockdown/${encodeURIComponent(token)}`,
                       reason.trim() ? { reason: reason.trim() } : {});
      setDone(true);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="pl" data-testid="public-lockdown-page">
      <div className="pl__card">
        <div className="pl__hero">
          <ShieldAlert className="w-6 h-6" />
          <h1>Secure my organization</h1>
        </div>

        <div className="pl__body">
          {loading ? (
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", color: "#475569", padding: "1rem 0" }}>
              <Loader2 className="w-4 h-4 animate-spin" /> Verifying secure link…
            </div>
          ) : error ? (
            <div data-testid="public-lockdown-error"
                 style={{ display: "flex", gap: "0.6rem", alignItems: "flex-start",
                            background: "#FEF2F2", border: "1px solid #FCA5A5",
                            padding: "0.85rem 1rem", borderRadius: 10 }}>
              <XCircle className="w-5 h-5" style={{ color: "#991B1B", flexShrink: 0, marginTop: 2 }} />
              <div>
                <div style={{ fontWeight: 700, color: "#991B1B" }}>Link cannot be used</div>
                <div style={{ fontSize: "0.85rem", color: "#7F1D1D", marginTop: "0.3rem" }}>
                  {error}
                </div>
                <div style={{ fontSize: "0.8rem", color: "#475569", marginTop: "0.6rem" }}>
                  If you still believe a recovery action was unexpected, contact
                  HarborWell Support directly.
                </div>
              </div>
            </div>
          ) : done ? (
            <div data-testid="public-lockdown-done"
                 style={{ display: "flex", gap: "0.6rem", alignItems: "flex-start",
                            background: "#F1F5F9", border: "1px solid #CBD5E1",
                            padding: "0.85rem 1rem", borderRadius: 10 }}>
              <CheckCircle2 className="w-5 h-5" style={{ color: "#166534", flexShrink: 0, marginTop: 2 }} />
              <div>
                <div style={{ fontWeight: 700, color: "#0F172A" }}>Lockdown complete</div>
                <div style={{ fontSize: "0.85rem", color: "#334155", marginTop: "0.35rem" }}>
                  Your organization has been secured. HarborWell Support has been
                  notified via the immutable audit log and will contact you to
                  investigate the incident and restore access when the situation
                  is understood.
                </div>
              </div>
            </div>
          ) : (
            <>
              <p className="pl__desc" data-testid="public-lockdown-intro">
                You are receiving this page because an unexpected administrative
                action on your HarborWell Partner Portal account may have
                occurred. Confirming below will immediately secure your
                organization.
              </p>

              <div className="pl__partner">
                <div className="pl__partner-label">Organization</div>
                <div className="pl__partner-name" data-testid="public-lockdown-partner">
                  {preview?.partner_name}
                </div>
                {preview?.expires_at && (
                  <div style={{ fontSize: "0.72rem", color: "#64748B", marginTop: "0.25rem" }}>
                    This secure link expires {new Date(preview.expires_at).toLocaleString()}
                  </div>
                )}
              </div>

              <div style={{ background: "#FFFBEB", border: "1px solid #FCD34D",
                              padding: "0.75rem 1rem", borderRadius: 10, fontSize: "0.82rem",
                              color: "#92400E", lineHeight: 1.5 }}>
                {preview?.action_summary}
              </div>

              <label style={{ display: "block", marginTop: "1.1rem",
                                fontSize: "0.78rem", color: "#334155", fontWeight: 600 }}>
                What happened? (optional, helps HarborWell Support investigate faster)
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                maxLength={500}
                placeholder="e.g., I received a security email but did not request any changes to my account."
                data-testid="public-lockdown-reason"
                style={{ width: "100%", padding: "0.6rem 0.75rem", border: "1px solid #CBD5E1",
                            borderRadius: 8, fontSize: "0.82rem", fontFamily: "inherit",
                            marginTop: "0.35rem", resize: "vertical" }}
              />

              <div className="pl__actions">
                <button
                  onClick={confirm}
                  disabled={submitting}
                  data-testid="public-lockdown-confirm"
                  className="pr-btn pr-btn--danger"
                >
                  {submitting
                    ? <Loader2 className="w-4 h-4 animate-spin" />
                    : <ShieldAlert className="w-4 h-4" />}
                  Secure my organization now
                </button>
              </div>

              <div className="pl__note">
                This is a one-time secure link. It cannot be reused after confirmation.
                No credentials, MFA codes, or personally identifiable information
                are transmitted through this page.
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
